<script setup>
import {useArticleApi} from '~/composables/api/useArticleApi'
import {useProductApi} from '~/composables/api/useProductApi'
import {useProductCategoryApi} from '~/composables/api/useProductCategoryApi'
import {prepareBlockFilters, prepareBlockSorts} from '~/utils/queryingUtils'
import {useArticleCategoryApi} from '~/composables/api/useArticleCategoryApi'
import {
  normalizeItemFromArticle,
  normalizeItemFromArticleCategory,
  normalizeItemFromProduct,
  normalizeItemFromProductCategory
} from '~/utils/pageUtils'
import ProductVariantSelector from '~/components/partials/subelements/ProductVariantSelector.vue'
import {useCartStore} from '~/store/cart'
import {cloneDeep} from 'lodash'
import {useNotification} from '~/composables/useNotification'

const props = defineProps({
  data: {
    type: Object,
    default: () => null
  }
})

const notification = useNotification()
const cartStore = useCartStore()
const articleApi = useArticleApi()
const articleCategoryApi = useArticleCategoryApi()
const productApi = useProductApi()
const productCategoryApi = useProductCategoryApi()

const productVariantSelectorRef = ref(null)
const selectedVariant = ref(null)
const currentProduct = ref()
const addItemQty = ref(1)

const {data: response, error} = await useAsyncData(`block-${props.data.id}-data`, () => {
  const blockContent = props.data.content
  const limit = blockContent?.limit || 10
  const page = blockContent?.page || 1
  const blog = blockContent.blog || null
  const sortField = prepareBlockSorts(blockContent)
  const filterField = prepareBlockFilters(blockContent)

  if (blockContent.source === 'Article') {
    return articleApi.getList(blog, sortField, filterField, page, limit)
  }

  if (blockContent.source === 'ArticleCategory') {
    return articleCategoryApi.getList(limit, sortField, filterField)
  }

  if (blockContent.source === 'Product') {
    return productApi.getList(limit, sortField, filterField)
  }

  if (blockContent.source === 'ProductCategory') {
    return productCategoryApi.getList(limit, sortField, filterField)
  }
})

const blockContent = computed(() => {
  const data = Array.isArray(response.value?.items) ? cloneDeep(response.value?.items) : response.value
  props.data.content.items = data || []
  return props.data.content
})
const blockContentLayout = computed(() => blockContent.value.type)
const mustShowTitle = computed(() => blockContent.value.showTitle)
const haveVSpacing = computed(() => blockContent.value.vSpacing)
const loadedItems = computed(() => {
  if (blockContent.value.source === 'Article') {
    return blockContent.value.items.map(item => normalizeItemFromArticle(item))
  }
  if (blockContent.value.source === 'ArticleCategory') {
    return blockContent.value.items.map(item => normalizeItemFromArticleCategory(item))
  }
  if (blockContent.value.source === 'Product') {
    return blockContent.value.items.map(item => normalizeItemFromProduct(item))
  }
  if (blockContent.value.source === 'ProductCategory') {
    return blockContent.value.items.map(item => normalizeItemFromProductCategory(item))
  }
})
const hasProductVariants = computed(() => {
  if (!loadedItems.value || loadedItems.value.length === 0) return false

  return blockContent.value?.source === 'Product' && loadedItems.value.some(item => item?.variants?.length > 0)
})
const firstItem = computed(() => loadedItems.value?.[0] || null)
const secondItem = computed(() => loadedItems.value?.[1] || null)
const thirdItem = computed(() => loadedItems.value?.[2] || null)
const restItems = computed(() => loadedItems.value?.slice(3) || [])
const gridCols = computed(() => {
  if (blockContentLayout.value === 'five-20') return 'grid-cols-10'

  return 'grid-cols-12'
})
/*const containerClasses = computed(() => {
  const classList = []

  if (!haveVSpacing.value) classList.push('mb-1')
  if (haveVSpacing.value) classList.push('my-6')
  classList.push(gridCols.value)

  return classList
})*/
const containerClasses = computed(() => {
  const columns = blockContent.value?.columns || 12
  const classList = {
    'mb-1': !haveVSpacing.value,
    'my-y': haveVSpacing.value,
    'col-span-12 sm:col-span-3': columns === 3,
    'col-span-12 sm:col-span-4': columns === 4,
    'col-span-12 sm:col-span-6': columns === 6,
    'col-span-12 sm:col-span-8': columns === 8,
    'col-span-12 sm:col-span-10': columns === 10,
    'col-span-12 sm:col-span-12': columns === 12
  }
  classList[gridCols.value] = true

  return classList
})

function variantSelected(variantId) {
  if (!variantId) return

  selectedVariant.value = variantId
  addToCart(currentProduct.value)
}

const addToCart = (item, $event = null) => {
  currentProduct.value = item
  const currentProductHasVariants = item?.variants?.length > 0

  if (currentProductHasVariants && !selectedVariant.value) {
    productVariantSelectorRef.value.open($event)
    return
  }

  if (currentProductHasVariants && !selectedVariant.value) {
    notification.showSuccess('Debes seleccionar una opción para agregar el producto al carrito')
    return
  }

  const itemToAdd = {
    product: cloneDeep(item.product),
    qty: addItemQty.value,
    selected_variant: selectedVariant.value?.id || null
  }
  cartStore.addItem(itemToAdd)

  selectedVariant.value = null
  notification.showSuccess('Producto añadido al carrito')
}
</script>

<template>
  <div v-if="blockContent" class="grid gap-1" :class="containerClasses">

    <!-- NO SE USA: inicio -->
    <template v-if="mustShowTitle">
      <div class="col-span-12 text-center mb-2">
        <h3 class="mt-12 mb-5 text-2xl md:text-4xl lg:text-5xl xl:text-5xl italic font-bold underline">
          {{ blockContent.title }}</h3>
      </div>
    </template>
    <template v-if="blockContentLayout === 'one-100'">
      <div v-if="firstItem" class="col-span-12 relative order-1 xl:order-1">
        <img class="w-full" :src="firstItem.image" :alt="firstItem.title" loading="lazy"/>
        <div class="absolute bottom-5 md:bottom-10 xl:bottom-10 text-white w-full text-center">
          <h2 class="px-1 text-2xl md:text-3xl lg:text-5xl xl:text-7xl uppercase font-bold italic cursor-pointer">
            {{ firstItem.title }}
          </h2>
          <p v-if="firstItem?.excerpt"
             class="px-1 text-xs md:text-md lg:text-lg xl:text-2xl uppercase font-bold italic mt-1 xl:mt-3 mb-3 xl:mb-10">
            {{ firstItem.excerpt }}</p>
          <a :href="firstItem.link"
             class="text-black hover:text-white text-xs md:text-md lg:text-lg xl:text-xl italic bg-white hover:bg-red-800 px-5 py-2">
            + Info
          </a>
        </div>
      </div>
    </template>

    <!-- 1 + 2 -->
    <template v-if="blockContentLayout === 'dynamic'">

      <div v-if="firstItem" class="col-span-12 xl:col-span-8 order-2 lg:order-2 p-2">
        <NuxtLink :href="firstItem.link">
          <img class="w-full" :src="firstItem.image" :alt="firstItem.title" loading="lazy"/>
        </NuxtLink>
        <div class="w-full text-center px-3 !text-[#363330] mb-5 py-3 lg:py-8">
          <h2 class="text-2xl lg:text-5xl font-bold italic px-4 text-justify">
            {{ firstItem.title }}
          </h2>
          <p
            class="text-xs text-justify lg:text-xl italic mt-3 mb-5 lg:mb-8 lg:mt-6 px-4">
            {{ firstItem.excerpt }}
          </p>
          <NuxtLink :href="firstItem.link"
                    class="text-[#dcd6ce] hover:text-[#dcd6ce] text-md md:text-md lg:text-lg xl:text-xl italic bg-[#363330] hover:bg-[3363330] px-5 py-2 !rounded-md font-bold">
            + Info
          </NuxtLink>
        </div>
      </div>

      <div class="col-span-12 xl:col-span-4 grid grid-cols-12 gap-1 order-3 xl:order-3 p-2">
        <div v-if="secondItem" class="col-span-12">
          <NuxtLink :href="secondItem.link">
            <img class="w-full" :src="secondItem.image" :alt="secondItem.title" loading="lazy"/>
          </NuxtLink>
          <div class="w-full text-center px-3 !text-[#363330] mb-5 py-3 lg:py-3">
            <h2 class="text-2xl lg:text-2xl font-bold italic px-4 text-justify">
              {{ secondItem.title }}
            </h2>
            <p
              class="text-sm text-justify lg:text-sm italic mt-3 mb-5 px-4">
              {{ secondItem.excerpt }}
            </p>
            <NuxtLink :href="secondItem.link"
                      class="text-[#dcd6ce] hover:text-[#dcd6ce] text-md md:text-md lg:text-lg xl:text-xl italic bg-[#363330] hover:bg-[3363330] px-5 py-2 !rounded-md font-bold">
              + Info
            </NuxtLink>
          </div>
        </div>

        <div v-if="thirdItem" class="col-span-12">
          <NuxtLink :href="thirdItem.link">
            <img class="w-full" :src="thirdItem.image" :alt="thirdItem.title" loading="lazy"/>
          </NuxtLink>
          <div class="w-full text-center px-3 !text-[#363330] mb-5 py-3 lg:py-3">
            <h2 class="text-2xl lg:text-2xl font-bold italic px-4 text-justify">
              {{ thirdItem.title }}
            </h2>
            <p
              class="text-sm text-justify lg:text-sm italic mt-3 mb-5 px-4">
              {{ thirdItem.excerpt }}
            </p>
            <NuxtLink :to="thirdItem.link"
                      class="text-[#dcd6ce] hover:text-[#dcd6ce] text-md md:text-md lg:text-lg xl:text-xl italic bg-[#363330] hover:bg-[3363330] px-5 py-2 !rounded-md font-bold">
              + Info
            </NuxtLink>
          </div>
        </div>
      </div>

      <div v-for="(item, index) in restItems" :key="`rest-item-${index}`"
           class="col-span-12 xl:col-span-4 relative order-4 xl:order-4">
        <NuxtLink :href="item.link">
          <img class="w-full" :src="item.image" :alt="item.title" loading="lazy"/>
        </NuxtLink>
        <div class="w-full text-center px-3 !text-[#363330] mb-5 py-3 lg:py-3">
          <h2 class="text-2xl lg:text-2xl font-bold italic px-4 text-justify">
            {{ item.title }}
          </h2>
          <p class="text-sm text-justify lg:text-sm italic mt-3 mb-5 px-4">
            {{ item.excerpt }}
          </p>
          <NuxtLink :to="item.link"
                    class="text-[#dcd6ce] hover:text-[#dcd6ce] text-md md:text-md lg:text-lg xl:text-xl italic bg-[#363330] hover:bg-[3363330] px-5 py-2 !rounded-md font-bold">
            + Info
          </NuxtLink>
        </div>
      </div>
    </template>

    <!-- NO SE USA -->
    <template v-if="blockContentLayout === 'three-33'">
      <div v-for="(item, index) in loadedItems" class="col-span-12 xl:col-span-4 mb-5 relative" :key="index">
        <h4 class="text-2xl md:text-3xl lg:text-3xl xl:text-2xl italic mb-1 text-center">
          {{ item.item }}
        </h4>
        <NuxtLink :to="item.link">
          <img class="w-full" :src="item.image" :alt="item.title" loading="lazy"/>
        </NuxtLink>
        <div class="flex justify-center absolute bottom-10 sm:bottom-20 xl:bottom-20 text-white w-full text-center gap-4">
          <NuxtLink
            v-if="item.type === 'product'"
            to="#"
            @click.prevent="($event) => addToCart(item, $event)"
            class="text-black hover:text-white text-xs md:text-md lg:text-lg xl:text-xs italic bg-white hover:bg-black px-5 py-2">
            Comprar
          </NuxtLink>
          <NuxtLink
            :to="item.link"
            class="text-black hover:text-white text-xs md:text-md lg:text-lg xl:text-xs italic bg-white hover:bg-black px-5 py-2">
            + Info
          </NuxtLink>
        </div>
      </div>
    </template>

    <template v-if="blockContentLayout === 'five-20'">
      <div v-for="(item, index) in loadedItems"
           class="col-span-5 lg:col-span-2 relative flex flex-col items-center cursor-pointer" :key="index">
        <NuxtLink :to="item.link">
          <img class="w-full" :src="item.image" :alt="item.title" loading="lazy">
        </NuxtLink>
        <div class="absolute uppercase font-bold text-white text-xl lg:text-3xl italic bottom-[10%]">
          <h4 class="px-4"><span class="bg-red-800 opacity-80">{{ item.title }}</span></h4>
        </div>
      </div>
    </template>

    <!-- CATEGORÍAS Y PRODUCTOS DESTACADOS HOME-->
    <template v-if="blockContentLayout === 'six-16'">
      <div v-for="(item, index) in loadedItems"
           class="col-span-6 lg:col-span-2 relative flex flex-col items-center cursor-pointer" :key="index">
        <NuxtLink :to="item.link">
          <img class="w-full" :src="item.image" :alt="item.title" loading="lazy">
        </NuxtLink>
        <div class="text-black text-[0.8rem] lg:text-[1rem] italic bottom-[10%] text-center mb-3 lg:mb-2">
          <h4 class="px-4"><span class="">{{ item.title }}</span></h4>
        </div>
        <div v-if="item.type === 'product'" class="flex justify-center bottom-10 sm:bottom-20 xl:bottom-20 text-white w-full text-center gap-4 mb-8">
          <NuxtLink
            :to="item.link"
            class="text-[#363330] hover:text-[#363330] bg-[#dcd6ce] hover:bg-[#dcd6ce] hover:font-bold text-xs md:text-md lg:text-lg xl:text-xs italic px-3 py-1">
            + Info
          </NuxtLink>
          <NuxtLink
            v-if="item.type === 'product'"
            to="#"
            @click.prevent="($event) => addToCart(item, $event)"
            class="text-white bg-red-800 hover:bg-red-800 hover:font-bold text-xs md:text-md lg:text-lg xl:text-xs italic px-3 py-1">
            Comprar
          </NuxtLink>
        </div>
      </div>
    </template>

    <!-- NO SE USA -->
    <template v-if="blockContentLayout === 'four-20'">
      <div v-for="(item, index) in loadedItems" class="col-span-12 xl:col-span-3 mb-8 relative" :key="index">
        <h4 class="text-xl md:text-2xl lg:text-2xl xl:text-2xl italic mb-1 text-center">
          {{ item.item }}
        </h4>
        <NuxtLink :to="item.link">
          <img class="w-full mb-4" :src="item.image" :alt="item.title" loading="lazy"/>
        </NuxtLink>
        <div class="absolute bottom-10 sm:bottom-20 xl:bottom-20 text-white w-full text-center">
          <NuxtLink
            :to="item.link"
            class="text-black hover:text-white text-xs md:text-md lg:text-lg xl:text-xs italic bg-white hover:bg-black px-5 py-2">
            + Info
          </NuxtLink>
        </div>
      </div>
    </template>

    <ProductVariantSelector
      ref="productVariantSelectorRef"
      v-if="hasProductVariants"
      :title="'Opciones ' + currentProduct?.title"
      :options="currentProduct?.variants"
      @input="variantSelected"
    />
  </div>
</template>
